import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '../services/supabase';
import { Helmet } from 'react-helmet';
import './ResetPassword.css';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(t);
  }, []);

  useEffect(() => {
    const checkSession = async () => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setError('Invalid or expired reset link. Please request a new password reset.');
      }
    };
    void checkSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (password.length < 6) {
      setError('Password must be at least 6 characters long.');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password,
      });

      if (updateError) {
        setError(updateError.message);
      } else {
        setSuccess(true);
        await supabase.auth.signOut();
      }
    } catch {
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const clearError = () => {
    if (error) {
      setError(null);
    }
  };

  const renderVisual = () => (
    <aside className="reset-page__visual" aria-hidden>
      <span className="reset-page__eyebrow">Secure Update</span>
      <h1 className="reset-page__title">New Password</h1>
      <p className="reset-page__lead">
        Choose a strong password to keep your account safe and get back to creating on DHH Collab.
      </p>

      <div className="reset-page__graphic">
        <div className="reset-page__shield">
          <span className="reset-page__lock-shackle" />
          <span className="reset-page__lock-body">
            <span className="reset-page__lock-keyhole" />
          </span>
        </div>
        <span className="reset-page__spark reset-page__spark--1" />
        <span className="reset-page__spark reset-page__spark--2" />
      </div>
    </aside>
  );

  if (success) {
    return (
      <div className={`reset-page ${visible ? 'reset-page--visible' : ''}`}>
        <Helmet>
          <title>DHH Collab | Password Reset Success</title>
          <meta name="description" content="Your password has been successfully reset." />
        </Helmet>

        <div className="reset-page__bg" aria-hidden>
          <span className="reset-page__blob reset-page__blob--1" />
          <span className="reset-page__blob reset-page__blob--2" />
          <span className="reset-page__grid" />
        </div>

        <div className="reset-page__inner container-padding">
          <div className="reset-page__layout">
            {renderVisual()}

            <div className="reset-page__card">
              <div className="reset-page__success">
                <div className="reset-page__success-box">
                  <p className="reset-page__success-title">Password updated!</p>
                  <p className="reset-page__success-text">
                    Your password has been changed successfully. Sign in with your new password to
                    continue.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="reset-page__submit reset-page__submit--ready"
                >
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`reset-page ${visible ? 'reset-page--visible' : ''}`}>
      <Helmet>
        <title>DHH Collab | Reset Password</title>
        <meta name="description" content="Set your new password." />
      </Helmet>

      <div className="reset-page__bg" aria-hidden>
        <span className="reset-page__blob reset-page__blob--1" />
        <span className="reset-page__blob reset-page__blob--2" />
        <span className="reset-page__grid" />
      </div>

      <div className="reset-page__inner container-padding">
        <div className="reset-page__layout">
          {renderVisual()}

          <div className="reset-page__card">
            <header className="reset-page__card-head">
              <h2 className="reset-page__card-title">Set new password</h2>
              <p className="reset-page__card-subtitle">Enter your new password below.</p>
            </header>

            {error && <div className="reset-page__error">{error}</div>}

            {!error?.includes('Invalid or expired') ? (
              <form className="reset-page__form" onSubmit={handleSubmit}>
                <div className="reset-page__fields">
                  <div className="reset-page__field">
                    <label htmlFor="password" className="reset-page__label">
                      New password
                    </label>
                    <input
                      id="password"
                      name="password"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={password}
                      onChange={e => {
                        setPassword(e.target.value);
                        clearError();
                      }}
                      className="reset-page__input"
                      placeholder="New password"
                    />
                  </div>
                  <div className="reset-page__field">
                    <label htmlFor="confirmPassword" className="reset-page__label">
                      Confirm new password
                    </label>
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      autoComplete="new-password"
                      required
                      value={confirmPassword}
                      onChange={e => {
                        setConfirmPassword(e.target.value);
                        clearError();
                      }}
                      className="reset-page__input"
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className={`reset-page__submit ${
                    loading ? 'reset-page__submit--disabled' : 'reset-page__submit--ready'
                  }`}
                >
                  {loading ? 'Updating...' : 'Update password'}
                </button>

                <Link to="/login" className="reset-page__link">
                  Back to login
                </Link>
              </form>
            ) : (
              <div className="reset-page__card-footer">
                <Link to="/forgot-password" className="reset-page__link">
                  Request a new reset link
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
